import { Box, BoxProps } from '@mui/material'
import { Component, ComponentType, createContext, PropsWithChildren, ReactNode } from 'react'
import { ApiAlertContext } from '../api-context'
import { IFormBaseContext, ISetModelStateFunc } from './types'
import { convertFormDataToJson, getErrorCommonFromResponse, getErrorFromResponse, singleValidate, trimAllStrings, validateMerge } from './helpers'
import FormValidator, { IPartialError, SingleRuleValidate } from './validator'

export interface IFormBaseSlots {
  formProps?: Omit<BoxProps, 'ref' | 'onSubmit' | 'component'> & { [key: string]: any }
}

export interface IFormBaseConfigs<T> {
  validate?: FormValidator<Partial<T>>
  dataTrimed?: boolean
  dataGetter?: (data: Partial<T>) => Partial<T>
}

export interface IFormBaseParams<T> extends IFormBaseConfigs<T> {}

export interface IFormBaseProps<T> extends PropsWithChildren, IFormBaseConfigs<T> {
  className?: string
  onSubmit: (data: Partial<T>, e: React.FormEvent<HTMLFormElement>) => Promise<void>
  onError?: (error: IPartialError<T>) => void
  slots?: IFormBaseSlots
}

export interface IFormBaseState<T> {
  modelState?: Partial<T>
  messageErrors: IPartialError<T>
}

export interface IFormBaseResult<T> {
  Form: ComponentType<IFormBaseProps<T>>
  Validator?: FormValidator<Partial<T>>
  Context: React.Context<IFormBaseContext<T>>
  mapContext: (params: (context: IFormBaseContext<T>) => JSX.Element) => JSX.Element
}

const createFormBase = function <T>(params?: IFormBaseParams<T>): IFormBaseResult<T> {
  const FormBaseContext = createContext<IFormBaseContext<T>>({
    setModelState: () => {},
    messageErrors: {},
    onBlur: () => {},
    setError: () => {},
    removeError: () => {},
    clearErrorAll: () => {}
  })

  class FormBase extends Component<IFormBaseProps<T>, IFormBaseState<T>> {
    refForm: HTMLFormElement | null = null
    constructor(props: IFormBaseProps<T>) {
      super(props)
      this.validate = this.getValidate()
      this.state = { messageErrors: {} }
    }

    render() {
      const rootClass = [this.props.className, this.props.slots?.formProps?.className].filter(Boolean)
      return (
        <Box
          component='form'
          ref={(ref: HTMLFormElement) => (this.refForm = ref)}
          onSubmit={this.onSubmit}
          {...(this.props.slots?.formProps as any)}
          className={rootClass.join(' ')}
        >
          <FormBaseContext.Provider value={this.mapContext()}>{this.props.children}</FormBaseContext.Provider>
        </Box>
      )
    }

    private mapContext = (): IFormBaseContext<T> => {
      const { setError, removeError, setModelState, clearErrorAll, onBlur } = this
      const { modelState, messageErrors } = this.state
      return { modelState, messageErrors, setError, onBlur, clearErrorAll, setModelState, removeError }
    }

    setModelState: ISetModelStateFunc<T> = (state) => {
      const obj = Object.assign({}, this.state.modelState, state)
      this.setState({ modelState: obj })
    }

    getValidate = (): FormValidator<Partial<T>> => {
      const defaultValidate = new FormValidator<Partial<T>>({})
      return validateMerge(defaultValidate, params?.validate, this.props.validate)
    }

    setError = (keyName: keyof T, message: string) => {
      const error = { [keyName]: [{ rule: SingleRuleValidate.Custom, message }] }
      this.setState({ messageErrors: Object.assign({}, this.state.messageErrors, error) })
    }

    removeError = (keyNames: keyof T | (keyof T)[]) => {
      const newErrors = { ...this.state.messageErrors }
      if (Array.isArray(keyNames)) {
        keyNames.forEach((key) => {
          if (newErrors[key]) delete newErrors[key]
        })
      } else {
        if (newErrors[keyNames]) delete newErrors[keyNames]
      }
      this.setState({ messageErrors: newErrors })
    }

    clearErrorAll = () => {
      this.setState({ messageErrors: {} })
    }

    private validate: FormValidator<Partial<T>>
    onSubmit: React.FormEventHandler<HTMLFormElement> = async (e) => {
      e.preventDefault()
      const formData = new FormData(e.currentTarget as HTMLFormElement)
      const raw = convertFormDataToJson<T>(formData)
      // TODO: validate raw data
      const trimmed = trimAllStrings(raw)
      this.setState({ modelState: trimmed })
      const messageErrors = this.validate.run(trimmed) as IPartialError<T>

      if (this.props.onError && Object.keys(messageErrors).length > 0) {
        this.props.onError(messageErrors)
      }

      if (messageErrors) {
        this.setState({ messageErrors: messageErrors })
        if (Object.keys(messageErrors).length > 0) return
      }
      try {
        await this.props.onSubmit(trimmed, e)
      } catch (error: any) {
        const errorCommon = getErrorCommonFromResponse(error as any)
        if (errorCommon.length > 0) ApiAlertContext.ApiAlert?.PushError(errorCommon[0])
        const messageError = getErrorFromResponse(error, trimmed)
        this.setState({ messageErrors: { ...this.state.messageErrors, ...(messageError || {}) } })
        // throw error
      }
    }

    onBlur = (keyName: keyof T) => {
      if (!this.refForm) return
      const { messageErrors } = this.state
      const formData = new FormData(this.refForm)
      const raw = convertFormDataToJson(formData)
      const trimmed = trimAllStrings(raw)
      this.setState({ modelState: trimmed })
      const error = singleValidate<T, Partial<T>>(keyName, trimmed, messageErrors, this.validate) || {}
      this.setState({ messageErrors: error as IPartialError<T> })
    }
  }

  return {
    Form: FormBase,
    Validator: params?.validate,
    Context: FormBaseContext,
    mapContext: (params: (context: IFormBaseContext<T>) => ReactNode) => <FormBaseContext.Consumer>{params}</FormBaseContext.Consumer>
  }
}
export default createFormBase
